import AssetPriceSession from './components/marketData/AssetPriceSession.jsx'
import { useEffect, useRef } from 'react'
import * as Sentry from '@sentry/react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './hooks/useAuth'
import { SessionKeepAlive } from './hooks/useSessionKeepAlive'
import { AutoSyncProvider } from './hooks/useAutoSync'
import { PrivacyProvider } from './hooks/usePrivacy'
import { ModeProvider } from './hooks/useMode'
import { NotificationsProvider } from './hooks/useNotifications.jsx'
import PriceAlertProvider from './components/priceAlerts/PriceAlertProvider.jsx'
import VwapNotificationBridge from './components/chart/VwapNotificationBridge.jsx'
import { useSettings } from './hooks/useSettings'
import { getLastVisitedPage } from './hooks/usePageTracking'
import { BookOpen } from 'lucide-react'
import Layout from './components/layout/Layout'
import AuthPage from './pages/AuthPage'
import Dashboard from './pages/Dashboard'
import Calculator from './pages/Calculator'
import AccountsSummary from './pages/AccountsSummary'
import AccountsSizing from './pages/AccountsSizing'
import AccountDetail from './pages/AccountDetail'
import PositionsCurrent from './pages/PositionsCurrent'
// Integrations page absorbed into Settings (EF-92). Route redirects below.
import SchwabCallback from './pages/SchwabCallback'
import PositionsClosed from './pages/PositionsClosed'
import SettingsPage from './pages/SettingsPage'
import Placeholder from './pages/Placeholder'
import TradeLog from './pages/TradeLog'
import TradeDetail from './pages/TradeDetail'
import ImportCSV from './pages/ImportCSV'
import Analytics from './pages/Analytics'
import PositionBoard from './pages/PositionBoard'
import StrategyLibrary from './pages/StrategyLibrary'
import RetiredStrategyWorkspace from './pages/RetiredStrategyWorkspace'
import PositionWorkspaceChartLab from './pages/__demo/PositionWorkspaceChartLab'
import CarsCalibration from './pages/__internal/CarsCalibration'
import { getRuntimeEnvironment } from './lib/runtimeEnvironment'
import { canonicalizePositionWorkspaceRoute } from './lib/positionWorkspaceRoute'

function ProtectedRoute({ children }) {
  const { user, loading, biometricPending } = useAuth()
  const prevUserRef = useRef(user?.id)

  if (user?.id !== prevUserRef.current) {
    prevUserRef.current = user?.id
  }

  if (loading) return (
    <div className="min-h-screen bg-parchment flex items-center justify-center">
      <div className="font-mono text-xs tracking-widest text-inkMuted uppercase animate-pulse">
        Loading...
      </div>
    </div>
  )
  if (!user || biometricPending) return <Navigate to="/auth" replace />
  return children
}

function SmartRedirect() {
  const { settings, loading } = useSettings()
  if (loading) return null

  // If a Schwab OAuth code was saved before redirecting to login, resume
  // the callback flow now that the user is authenticated.
  const savedCode = sessionStorage.getItem('schwab_oauth_code')
  if (savedCode) {
    return <Navigate to="/auth/schwab/callback" replace />
  }

  const defaultPage = settings?.default_page || 'last_visited'
  if (defaultPage === 'last_visited') {
    const lastPage = getLastVisitedPage()
    return <Navigate to={lastPage || '/dashboard'} replace />
  }
  return <Navigate to={defaultPage} replace />
}

function LegacyChartLabRedirect() {
  const location = useLocation()
  const target = canonicalizePositionWorkspaceRoute(`${location.pathname}${location.search || ''}`)
  return <Navigate to={`${target.pathname}${target.search}`} replace />
}

function AppRoutes() {
  const { user, biometricPending } = useAuth()
  return (
    <Routes>
      {/* Auth */}
      <Route path="/auth" element={user && !biometricPending ? <SmartRedirect /> : <AuthPage />} />

      {/* OAuth callback — NOT protected. Session may have expired between the
          Schwab redirect and the callback. The component handles saving the
          code to sessionStorage and resuming after login. */}
      <Route path="/auth/schwab/callback" element={<SchwabCallback />} />

      {/* Root redirect */}
      <Route path="/" element={<ProtectedRoute><SmartRedirect /></ProtectedRoute>} />

      {/* Protected app shell */}
      <Route path="/" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route path="dashboard"   element={<Dashboard />} />
        <Route path="calculator"  element={<Calculator />} />
        <Route path="integrations" element={<Navigate to="/settings" replace />} />
        <Route path="import" element={<ImportCSV />} />
        <Route path="settings"    element={<SettingsPage />} />

        {/* Journal & Analytics — placeholders */}
        <Route path="journal" element={
          <Placeholder
            title="Journal"
            description="Trade journaling, setup tagging, and performance notes coming soon."
            icon={BookOpen}
          />
        } />
        <Route path="analytics" element={<Analytics />} />

        {/* Accounts sub-pages */}
        <Route path="accounts">
          <Route index element={<Navigate to="/accounts/summary" replace />} />
          <Route path="summary" element={<AccountsSummary />} />
          <Route path=":accountNumber" element={<AccountDetail />} />
        </Route>

        {/* Positions sub-pages */}
        <Route path="positions">
          <Route index element={<Navigate to="/positions/current" replace />} />
          <Route path="current"  element={<PositionsCurrent />} />
          <Route path="board"    element={<PositionBoard />} />
          <Route path="workspace" element={<PositionWorkspaceChartLab />} />
          <Route path="board/:projectId" element={<PositionWorkspaceChartLab />} />
          <Route path="symbol/:symbol" element={<PositionWorkspaceChartLab />} />
          <Route path="planner"  element={<AccountsSizing />} />
          <Route path="history"  element={<TradeLog />} />
          <Route path="history/:tradeId" element={<TradeDetail />} />
          {/* The legacy Strategy Builder was retired after the security audit.
              Preserve old bookmarks without loading any retired code. */}
          <Route path="strategy-builder" element={<Navigate to="/strategies" replace />} />
          <Route path="strategy-builder/new" element={<Navigate to="/strategies" replace />} />
          <Route path="strategy-builder/:strategyId" element={<Navigate to="/strategies" replace />} />
        </Route>

        {/* Strategies — SE-WS-01 */}
        <Route path="strategies">
          <Route index element={<StrategyLibrary />} />
          <Route path=":strategyId" element={<RetiredStrategyWorkspace />} />
        </Route>

        <Route path="__demo/position-workspace-chart" element={<LegacyChartLabRedirect />} />
        <Route path="__internal/cars-calibration" element={<CarsCalibration />} />
      </Route>
    </Routes>
  )
}

function App() {
  useEffect(() => {
    document.title = getRuntimeEnvironment().browserTitle
  }, [])

  return (
    <AuthProvider>
      {/* EF-182: proactive Supabase session keep-alive. First child of
          <AuthProvider> so useAuth() resolves and the keep-alive is active
          for every page rendered downstream. */}
      <SessionKeepAlive />
      <AutoSyncProvider>
        <PrivacyProvider>
          <ModeProvider>
            <NotificationsProvider>
              <VwapNotificationBridge />
              <PriceAlertProvider><AssetPriceSession><AppRoutes /></AssetPriceSession></PriceAlertProvider>
            </NotificationsProvider>
          </ModeProvider>
        </PrivacyProvider>
      </AutoSyncProvider>
    </AuthProvider>
  )
}

export default Sentry.withErrorBoundary(App, {
  fallback: ({ resetError }) => (
    <div style={{ padding: '2rem', textAlign: 'center', color: '#e0e0e0', background: '#0a0a0a', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <h2 style={{ marginBottom: '1rem' }}>Something went wrong</h2>
      <p style={{ color: '#888', marginBottom: '1.5rem' }}>An unexpected error occurred. The error has been reported.</p>
      <button
        onClick={() => { resetError(); window.location.reload() }}
        style={{ padding: '0.5rem 1.5rem', background: '#161616', color: '#e0e0e0', border: '1px solid #333', borderRadius: '6px', cursor: 'pointer' }}
      >
        Reload Page
      </button>
    </div>
  ),
})
